/**
 * The single source of truth for chart series colors across the whole app.
 *
 * Every chart (bar, line, pie) must pick its series colors from {@link chartSeriesColors} via
 * {@link getSeriesColor}, in order, rather than choosing colors ad hoc - this is what keeps a
 * "first series" always reading as the same color everywhere, and keeps the palette itself
 * anchored to the corporate style guide (agents/guidance/style/STYLES.md) rather than to the
 * semantic success/warning/danger text tokens in tokens.css (which are tuned per-theme for text
 * contrast, and are not brand colors).
 *
 * Order: Hillier Pink, Hidcote Blue, Saffron, then the remaining primary/secondary palette
 * colors (Orange, Berry, Sky Blue, Deep Blue, Galaxy Purple).
 */
const chartSeriesColors: readonly string[] = [
  "var(--color-chart-pink)",
  "var(--color-chart-blue)",
  "var(--color-chart-yellow)",
  "var(--color-chart-orange)",
  "var(--color-chart-berry)",
  "var(--color-chart-sky-blue)",
  "var(--color-chart-deep-blue)",
  "var(--color-chart-purple)",
];

/** Returns the color for the series at `index`, cycling back to the start of the palette if there are more series than colors. */
export function getSeriesColor(index: number): string {
  return chartSeriesColors[index % chartSeriesColors.length];
}

/**
 * One colour per AI tool, matching the My Usage chart (Claude pink, Cursor blue, Copilot yellow), so a tool reads as the same
 * colour on every page and every card, whatever order the tools are listed in. A tool not listed takes the next spare brand colour.
 */
const vendorColors: Record<string, string> = {
  anthropic: "var(--color-chart-pink)",
  cursor: "var(--color-chart-blue)",
  github: "var(--color-chart-yellow)",
  bedrock: "var(--color-chart-orange)",
};
const spareVendorColors: readonly string[] = ["var(--color-chart-berry)", "var(--color-chart-sky-blue)", "var(--color-chart-deep-blue)"];

/** Returns the fixed colour for an AI tool, or a spare brand colour (chosen by `index`) for a tool with none of its own. */
export function getVendorColor(vendorId: string, index: number): string {
  return vendorColors[vendorId.toLowerCase()] ?? spareVendorColors[index % spareVendorColors.length];
}

/**
 * Returns a Recharts `Tooltip` `itemSorter` that keeps tooltip rows in `dataKeyOrder` - the same
 * order the series/bars/lines were declared in and the legend renders them in - instead of
 * Recharts' default behaviour of sorting tooltip rows by value, which otherwise makes the tooltip
 * disagree with the legend and chart every time the highest-spending series isn't the first one
 * declared.
 */
export function createSeriesOrderTooltipSorter(
  dataKeyOrder: readonly string[],
): (item: { dataKey?: string | number | ((obj: unknown) => unknown) }) => number {
  return (item) => {
    const key = typeof item.dataKey === "function" ? undefined : item.dataKey;
    const index = dataKeyOrder.indexOf(String(key));
    return index === -1 ? dataKeyOrder.length : index;
  };
}
